'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

interface DeferUntilVisibleProps {
  children: ReactNode;
  /** Rendered until the wrapper scrolls within `rootMargin` of the viewport. */
  placeholder?: ReactNode;
  /** How far ahead of the viewport to begin mounting children. Default '200px'. */
  rootMargin?: string;
  /** Class for the wrapper element — reserve the final height here to avoid CLS. */
  className?: string;
}

/**
 * Defers mounting — and therefore hydrating — its children until the wrapper
 * scrolls within `rootMargin` of the viewport. Use it to keep below-the-fold
 * interactive islands off the initial-load critical path, which cuts Total
 * Blocking Time. (ServiceLocationsMap uses this to defer the ~260 KiB Google
 * Maps JS API in the same way.)
 *
 * Children are NOT server-rendered into the initial HTML, so only use this for
 * below-the-fold content where that's acceptable, and pass a `placeholder` that
 * reserves the final height to avoid layout shift (CLS).
 */
export default function DeferUntilVisible({
  children,
  placeholder = null,
  rootMargin = '200px',
  className,
}: DeferUntilVisibleProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Start hidden on both server and client so the initial render matches (no
  // hydration mismatch); the observer reveals children once near the viewport.
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;

    const el = ref.current;
    if (!el) return;

    // Environments without IntersectionObserver mount immediately.
    if (typeof IntersectionObserver === 'undefined') {
      const id = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(id);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShow(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [show, rootMargin]);

  return (
    <div
      ref={ref}
      className={className}
    >
      {show ? children : placeholder}
    </div>
  );
}
